/*
 * Modal de registro de jornada: muestra StartTimeRecordForm o EndTimeRecordForm
 * segun si hay un worklog pendiente.
 */
import React, { useEffect, useState } from 'react'
import { Modal, Spin, Button, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import StartTimeRecordForm from '../forms/startTimeRecordForm'
import EndTimeRecordForm from '../forms/endTimeRecordForm'
import {
  SortOrder,
  Worklog,
  WorklogOrderByInput,
  WorklogWhereInput
} from '../../../generated/graphql'
import { getPayload } from '../../../utils/auth'
import { useLazyQuery } from '@apollo/client'
import { WORKLOGS } from '../gql/verteOfficeGQL'
import CreateTimeRecordForm from '../forms/CreateTimeRecordForm'

interface TimeRecordModalProps {
  initialOpen?: boolean
  onClose?: () => void
}

interface WorklogOutputType {
  worklogs: Worklog[]
}

interface WorklogInputType {
  where: WorklogWhereInput
  orderBy: WorklogOrderByInput[]
}

type ModalView = 'selector' | 'start' | 'create'

export const WORKLOG_ID_KEY = 'current_worklog_id'

const TimeRecordModal: React.FC<TimeRecordModalProps> = ({
  initialOpen = true,
  onClose
}) => {
  const [getWorklogsById, { loading: loadingById, data: dataById }] =
    useLazyQuery<WorklogOutputType, WorklogInputType>(WORKLOGS, {
      fetchPolicy: 'no-cache'
    })

  const PAYLOAD = getPayload()
  const { t } = useTranslation()
  const [open, setOpen] = useState(initialOpen)
  const [view, setView] = useState<ModalView>('selector')

  const handleCancel = () => {
    setOpen(false)
    onClose?.()
  }

  useEffect(() => {
    try {
      if (PAYLOAD) {
        const worklogId = localStorage.getItem(WORKLOG_ID_KEY)

        if (worklogId) {
          getWorklogsById({
            variables: {
              where: {
                id: { equals: worklogId },
                user_id: { equals: PAYLOAD?.id },
                deleted_at: { equals: null }
              },
              orderBy: [{ created_at: SortOrder.Desc }]
            }
          })
        }
      }
    } catch (error) {
      console.error('Error getting payload:', error)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchedWorklog = dataById?.worklogs?.[0]
  const isFinished = fetchedWorklog != null && fetchedWorklog.end_time != null
  if (isFinished) {
    localStorage.removeItem(WORKLOG_ID_KEY)
  }
  const pendingWorklog = isFinished ? undefined : fetchedWorklog

  const ActionSelector: React.FC = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='middle'>
      <Button
        type='primary'
        block
        shape='round'
        onClick={() => setView('start')}
      >
        {t('new_activity')}
      </Button>
      <Button
        block
        shape='round'
        onClick={() => setView('create')}
      >
        {t('finished_activity')}
      </Button>
    </Space>
  )

  const renderContent = () => {
    if (loadingById) return <Spin />

    if (pendingWorklog != null) {
      return (
        <EndTimeRecordForm
          worklogId={pendingWorklog.id}
          startTime={pendingWorklog.start_time}
          onCancel={handleCancel}
          onClose={handleCancel}
        />
      )
    }

    if (view === 'start') {
      return <StartTimeRecordForm
        onCancel={() => setView('selector')}
        onClose={handleCancel}
        onBack={() => setView('selector')}
      />
    }

    if (view === 'create') {
      return (
        <CreateTimeRecordForm
          onCancel={() => setView('selector')}
          onClose={handleCancel}
          onBack={() => setView('selector')}
        />
      )
    }

    return <ActionSelector />
  }

  return (
    <Modal
      open={open}
      title={t('time_record')}
      onCancel={handleCancel}
      onOk={handleCancel}
      footer={null}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  )
}

export default TimeRecordModal
